import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { CdkPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
  AfterContentChecked,
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';

type TabPositionState = 'center' | 'left' | 'right' | 'left-origin-center' | 'right-origin-center';

@Component({
  selector: 'gc-custom-tab',
  templateUrl: './custom-tab.component.html',
  styleUrls: ['./custom-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CustomTabComponent implements OnInit {
  position: number = null;
  origin: number = null;
  isActive = false;
  @ViewChild(TemplateRef, { static: true }) _implicitContent: TemplateRef<any>;

  private contentPortal: TemplatePortal = null;
  constructor(private readonly viewContainerRef: ViewContainerRef) {}

  get content(): TemplatePortal {
    return this.contentPortal;
  }

  ngOnInit() {
    this.contentPortal = new TemplatePortal(this._implicitContent, this.viewContainerRef);
  }
}

@Component({
  selector: 'gc-custom-tab-container',
  templateUrl: './custom-tab-container.component.html',
  styleUrls: ['./custom-tab-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CustomTabContainerComponent implements AfterContentChecked, AfterContentInit, OnDestroy {
  @ContentChildren(CustomTabComponent)
  private readonly _tabs: QueryList<CustomTabComponent>;
  _selectedIndex: number;
  private _indexToSelect: number;
  private tabsSubscription: Subscription;
  constructor(private readonly cdRef: ChangeDetectorRef) {}

  @Input()
  get selectedIndex(): number {
    return this._selectedIndex;
  }
  set selectedIndex(value: number) {
    this._indexToSelect = value || 0;
  }

  get tabs(): QueryList<CustomTabComponent> {
    return this._tabs;
  }

  get currentTab(): CustomTabComponent {
    return this.tabs.toArray()[this.selectedIndex];
  }

  ngAfterContentChecked() {
    const indexToSelect = (this._indexToSelect = this.clampTabIndex(this._indexToSelect));

    Promise.resolve().then(() => {
      this._tabs.forEach((tab, index) => (tab.isActive = index === indexToSelect));
    });

    this._tabs.forEach((tab, index) => {
      tab.position = index - indexToSelect;

      if (this._selectedIndex !== null && tab.position === 0 && !tab.origin) {
        tab.origin = indexToSelect - this._selectedIndex;
      }
    });
    if (this._selectedIndex !== indexToSelect) {
      this._selectedIndex = indexToSelect;
      this.cdRef.markForCheck();
    }
  }

  ngAfterContentInit() {
    this.tabsSubscription = this._tabs.changes.subscribe(() => {
      const indexToSelect = this.clampTabIndex(this._indexToSelect);

      if (indexToSelect === this._selectedIndex) {
        const tabs = this._tabs.toArray();

        for (let i = 0; i < tabs.length; i++) {
          if (tabs[i].isActive) {
            this._indexToSelect = this._selectedIndex = i;
            break;
          }
        }
      }
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.tabsSubscription.unsubscribe();
  }

  private clampTabIndex(index: number): number {
    return Math.min(this._tabs.length - 1, Math.max(index || 0, 0));
  }
}

@Component({
  selector: 'gc-custom-tab-body',
  templateUrl: './custom-tab-body.component.html',
  styleUrls: ['./custom-tab-body.component.scss'],
  animations: [
    trigger('translateTab', [
      state('center, void, left-origin-center, right-origin-center', style({ transform: 'none' })),
      state(
        'left',
        style({
          transform: 'translate3d(-100%, 0, 0)',
          minHeight: '1px',
        }),
      ),
      state(
        'right',
        style({
          transform: 'translate3d(100%, 0, 0)',
          minHeight: '1px',
        }),
      ),
      transition('* => left, * => right, left => center, right => center', animate('300ms cubic-bezier(0.35, 0, 0.25, 1)')),
      transition('void => left-origin-center', [
        style({ transform: 'translate3d(-100%, 0, 0)' }),
        animate('300ms cubic-bezier(0.35, 0, 0.25, 1)'),
      ]),
      transition('void => right-origin-center', [
        style({ transform: 'translate3d(100%, 0, 0)' }),
        animate('300ms cubic-bezier(0.35, 0, 0.25, 1)'),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CustomTabBodyComponent implements OnInit, OnDestroy {
  private positionIndex: number;
  _position: TabPositionState = null;

  @ViewChild(CdkPortalOutlet)
  _portalHost: CdkPortalOutlet;

  @Input() origin: number;
  @Input() content: TemplatePortal;

  @Output() readonly _onCentering: EventEmitter<number> = new EventEmitter<number>();
  @Output() readonly _beforeCentering: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() readonly _afterLeavingCenter: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly _onCentered: EventEmitter<void> = new EventEmitter<void>();

  _translateTabComplete = new Subject<AnimationEvent>();

  constructor(private readonly elementRef: ElementRef) {
    this._translateTabComplete
      .pipe(distinctUntilChanged((x, y) => x.fromState === y.fromState && x.toState === y.toState))
      .subscribe((event) => {
        if (this._isCenterPosition(event.toState) && this._isCenterPosition(this._position)) {
          this._onCentered.emit();
        }

        if (this._isCenterPosition(event.fromState) && !this._isCenterPosition(this._position)) {
          this._afterLeavingCenter.emit();
        }
      });
  }

  @Input()
  set position(position: number) {
    this.positionIndex = position;
    this.computePositionAnimationState();
  }

  ngOnInit() {
    if (this._position === 'center' && this.origin != null) {
      this._position = this.computePositionFromOrigin(this.origin);
    }
  }

  ngOnDestroy() {
    this._translateTabComplete.complete();
  }

  private computePositionAnimationState() {
    if (this.positionIndex < 0) {
      this._position = 'left';
    } else if (this.positionIndex > 0) {
      this._position = 'right';
    } else {
      this._position = 'center';
    }
  }

  private computePositionFromOrigin(origin: number): TabPositionState {
    return origin >= 0 ? 'left-origin-center' : 'right-origin-center';
  }

  _isCenterPosition(position: TabPositionState | string): boolean {
    return ['center', 'left-origin-center', 'right-origin-center'].includes(position);
  }

  _onTranslateTabStart(event: AnimationEvent) {
    const isCentering = this._isCenterPosition(event.toState);
    this._beforeCentering.emit(isCentering);
    if (isCentering) {
      this._onCentering.emit(this.elementRef.nativeElement.clientHeight);
    }
  }
}

@Directive({
  selector: '[gcTabBodyHost]',
  standalone: false,
})
export class CustomTabBodyHostDirective extends CdkPortalOutlet implements OnInit, OnDestroy {
  private centeringSub = Subscription.EMPTY;
  private leavingSub = Subscription.EMPTY;

  constructor(
    componentFactoryResolver: ComponentFactoryResolver,
    viewContainerRef: ViewContainerRef,
    @Inject(forwardRef(() => CustomTabBodyComponent))
    private readonly host: CustomTabBodyComponent,
  ) {
    super(componentFactoryResolver, viewContainerRef);
  }

  ngOnInit() {
    super.ngOnInit();

    this.centeringSub = this.host._beforeCentering
      .pipe(startWith(this.host._isCenterPosition(this.host._position)))
      .subscribe((isCentering: boolean) => {
        if (isCentering && !this.hasAttached()) {
          this.attach(this.host.content);
        }
      });

    this.leavingSub = this.host._afterLeavingCenter.subscribe(() => {
      this.detach();
    });
  }
  ngOnDestroy() {
    super.ngOnDestroy();
    this.centeringSub.unsubscribe();
    this.leavingSub.unsubscribe();
  }
}
